import { baseApi } from "@/shared/api";
import type {
  GuestInvitationDetail,
  GuestInvitationResendResponse,
} from "../types/mobileSigning";

interface SourceArgs {
  sourceType: string;
  sourceId: number;
}

interface ResendArgs extends SourceArgs {
  locale?: string;
}

function invitationTag(args: SourceArgs) {
  return { type: "GuestInvitations" as const, id: `${args.sourceType}:${args.sourceId}` };
}

function sourceUrl({ sourceType, sourceId }: SourceArgs, suffix = "") {
  return `/api/signing/guest-invitations/source/${sourceType}/${sourceId}/${suffix}`;
}

export const guestInvitationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGuestInvitationBySource: builder.query<GuestInvitationDetail, SourceArgs>({
      query: (args) => ({
        url: sourceUrl(args),
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, args) => [invitationTag(args)],
    }),

    resendGuestInvitation: builder.mutation<GuestInvitationResendResponse, ResendArgs>({
      query: ({ locale, ...args }) => ({
        url: sourceUrl(args, "resend/"),
        method: "POST",
        body: locale ? { locale } : {},
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { locale: _locale, ...args }) => [invitationTag(args)],
    }),

    revokeGuestInvitation: builder.mutation<GuestInvitationDetail, SourceArgs>({
      query: (args) => ({
        url: sourceUrl(args, "revoke/"),
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, args) => [invitationTag(args)],
    }),
  }),
});

export const {
  useGetGuestInvitationBySourceQuery,
  useLazyGetGuestInvitationBySourceQuery,
  useResendGuestInvitationMutation,
  useRevokeGuestInvitationMutation,
} = guestInvitationsApi;
